/**
 * Discovery Query Keys
 *
 * Centralized query key factory for the discovery tab.
 * The discovery tab is the canonical view for notification workflow data,
 * replacing the scattered correspondenceKeys.workflowData usage.
 */

/**
 * Discovery query key factory
 *
 * @example
 * ```typescript
 * // Fetch discovery data for a matter
 * useQuery({ queryKey: discoveryKeys.data(matterId), queryFn: ... })
 *
 * // Invalidate all discovery data for a matter
 * invalidatePrefix(queryClient, discoveryKeys.forMatter(matterId))
 * ```
 */
export const discoveryKeys = {
  /**
   * Root key for all discovery queries
   */
  all: ['discovery'] as const,

  /**
   * Base key for a specific matter's discovery data
   * Invalidating this clears all discovery caches for the matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) => [...discoveryKeys.all, matterId] as const,

  /**
   * Full workflow data for the discovery tab
   * Contains institutions, correspondences, documents, signatories, lawFirm, matter
   * @param matterId - The matter ID
   */
  data: (matterId: number) =>
    [...discoveryKeys.forMatter(matterId), 'data'] as const
} as const;
